import { useAuth as useOidcAuth } from 'react-oidc-context';
import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import httpClient from '../services/http-client';
import { UserService } from '../services/user-service';
import { UserSessionStorage } from '../services/user-session-storage';
import { getActiveOidcStorageKey } from '../config/oidc-config';

const userService = new UserService(httpClient);

// Helper function to decode JWT token
function decodeJwtPayload(token: string) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT token:', error);
    return {};
  }
}

export function useAuth() {
  const auth = useOidcAuth();

  // Log initialization and state changes
  useEffect(() => {
    console.log('[useAuth] Auth state changed:', {
      isLoading: auth.isLoading,
      isAuthenticated: auth.isAuthenticated,
      hasUser: !!auth.user,
      hasError: !!auth.error,
      errorType: auth.error ? (auth.error as any).message : null
    });
    
    // Check what's in localStorage on every state change
    const oidcKey = getActiveOidcStorageKey();
    const storedOidc = localStorage.getItem(oidcKey);
    if (storedOidc && !auth.isLoading) {
      try {
        const parsed = JSON.parse(storedOidc);
        const now = Date.now() / 1000;
        const expired = parsed.expires_at ? (now > parsed.expires_at) : true;
        console.log('[useAuth] localStorage has OIDC data:', {
          username: parsed.profile?.preferred_username,
          hasAccessToken: !!parsed.access_token,
          tokenLength: parsed.access_token?.length || 0,
          expiresAt: parsed.expires_at ? new Date(parsed.expires_at * 1000).toISOString() : 'N/A',
          expired: expired,
          secondsUntilExpiry: parsed.expires_at ? Math.floor(parsed.expires_at - now) : 'N/A'
        });
        
        if (!auth.isAuthenticated && !expired && parsed.access_token) {
          console.error('[useAuth] ⚠️ PROBLEM: localStorage has valid token but auth.isAuthenticated is FALSE!');
          console.error('[useAuth] This indicates the OIDC library is not recognizing the manually-set session');
        }
      } catch (e) {
        console.error('[useAuth] Failed to parse stored OIDC data:', e);
      }
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.error]);

  // Extract additional claims from the access token
  const tokenClaims = useMemo(() => {
    if (auth.user?.access_token) {
      const claims = decodeJwtPayload(auth.user.access_token);
      console.log('[useAuth] Decoded token claims:', {
        sub: claims.sub,
        email: claims.email,
        preferred_username: claims.preferred_username,
        roles: claims.roles,
        hasRealmAccess: !!claims.realm_access,
      });
      return claims;
    }
    return {};
  }, [auth.user?.access_token]);

  // System-wide roles (approvn-admin/approvn-approver/approvn-customer-support/approvn-picker) are
  // managed locally in the DB, not sourced from the Okta/Keycloak JWT "roles" claim (which can't be
  // relied on - see UserSystemRole/SystemRoleNames in approvn-api).
  const { data: roles = [] } = useQuery({
    queryKey: ['current-user-system-roles'],
    queryFn: () => userService.getCurrentUserRoles(),
    enabled: auth.isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  // Check if user is an admin (DivisionAdmin is treated as admin-equivalent everywhere else -
  // backend RequireDivisionAdmin policy, use-user-roles.ts hasAnyRole checks)
  const isAdmin = useMemo(() => {
    return roles.includes('Admin') || roles.includes('approvn-admin') || roles.includes('SystemAdmin') || roles.includes('DivisionAdmin');
  }, [roles]);

  // Check if user has Support role (read-only visibility, no writes, no config/simulator)
  const isSupport = useMemo(() => {
    return roles.includes('approvn-customer-support');
  }, [roles]);

  // WorkflowAdmin is a narrower division-scoped role than DivisionAdmin - limited to editing
  // workflows and users (see RequireWorkflowAdmin policy in approvn-api). Deliberately NOT folded
  // into isAdmin, so Configuration/Simulator/Approver Report/All Orders stay admin-only.
  const isWorkflowAdmin = useMemo(() => {
    return roles.includes('WorkflowAdmin');
  }, [roles]);

  useEffect(() => {
    // Set up the access token getter for the HTTP client
    httpClient.setAccessTokenGetter(() => {
      return auth.user?.access_token || null;
    });
  }, [auth.user?.access_token]);

  useEffect(() => {
    // Log auth errors to console for debugging
    // Ignore "No matching state" errors - these happen when manually switching sessions
    if (auth.error) {
      const errorMessage = typeof auth.error === 'object' && auth.error !== null && 'message' in auth.error 
        ? String(auth.error.message) 
        : String(auth.error);
      
      if (errorMessage.includes('No matching state found in storage')) {
        // This is expected when we manually restore a session during user switching
        console.log('[useAuth] Ignoring expected OIDC state error during session switch');
        return;
      }
      
      console.error('OIDC Auth Error:', auth.error);
    }
  }, [auth.error]);

  useEffect(() => {
    // Log token claims for debugging
    if (tokenClaims && Object.keys(tokenClaims).length > 0) {
      console.log('JWT Token Claims:', tokenClaims);
      console.log('Extracted names:', {
        given_name: tokenClaims.given_name,
        family_name: tokenClaims.family_name,
        preferred_username: tokenClaims.preferred_username,
        email: tokenClaims.email
      });
      console.log('Division claims:', {
        divisionId: tokenClaims.divisionId,
        division_id: tokenClaims.division_id
      });
    }
  }, [tokenClaims]);

  // Save user session when authenticated
  // Using a ref to track the last saved username to prevent duplicate saves
  const lastSavedUsername = useRef<string | null>(null);
  
  useEffect(() => {
    // Get username from either preferred_username (profile) or sub (token)
    const username = auth.user?.profile?.preferred_username || auth.user?.profile?.sub || tokenClaims?.sub;
    
    console.log('[useAuth] Session save effect triggered:', {
      isAuthenticated: auth.isAuthenticated,
      hasUser: !!auth.user,
      hasTokenClaims: !!tokenClaims && Object.keys(tokenClaims).length > 0,
      hasProfile: !!auth.user?.profile,
      profilePreferredUsername: auth.user?.profile?.preferred_username,
      profileSub: auth.user?.profile?.sub,
      tokenSub: tokenClaims?.sub,
      resolvedUsername: username,
      lastSaved: lastSavedUsername.current,
    });
    
    if (auth.isAuthenticated && auth.user && tokenClaims && Object.keys(tokenClaims).length > 0 && username) {
      
      // Check if we just added a new account
      const addingAccount = sessionStorage.getItem('approvn_adding_account');
      const previousUser = sessionStorage.getItem('approvn_previous_user');
      
      if (addingAccount === 'true') {
        console.log('========================================');
        console.log('[useAuth] 🎉 NEW ACCOUNT ADDED');
        console.log('========================================');
        console.log('[useAuth] Previous user:', previousUser);
        console.log('[useAuth] New user:', username);
        
        // IMPORTANT: Clear the flags IMMEDIATELY to prevent any switching back
        sessionStorage.removeItem('approvn_adding_account');
        sessionStorage.removeItem('approvn_previous_user');
        sessionStorage.removeItem('approvn_redirect_to_login');
        
        console.log('[useAuth] Both accounts are now available for switching!');
        console.log('[useAuth] Current active user should be:', username);
        
        // Verify the OIDC storage has the correct user
        const oidcKey = getActiveOidcStorageKey();
        const storedOidc = localStorage.getItem(oidcKey);
        if (storedOidc) {
          const parsed = JSON.parse(storedOidc);
          const storedUsername = parsed.profile?.preferred_username || parsed.profile?.sub;
          if (storedUsername !== username) {
            console.error('[useAuth] ⚠️ MISMATCH! OIDC storage has:', storedUsername, 'but should have:', username);
          } else {
            console.log('[useAuth] ✅ OIDC storage correctly has:', storedUsername);
          }
        }
      }
      
      // Only save if this is a new user or if we haven't saved this user yet this session
      if (lastSavedUsername.current === username) {
        console.log('[useAuth] ⏭️  Skipping duplicate save for:', username);
        console.log('[useAuth]   lastSavedUsername.current:', lastSavedUsername.current);
        console.log('[useAuth]   This is intentional to prevent multiple saves per session');
        return;
      }
      
      console.log('[useAuth] ✅ Conditions met - proceeding with save:', username);
      console.log('[useAuth]   This is a new save (lastSavedUsername was:', lastSavedUsername.current, ')');
      
      const sessionData = {
        id: tokenClaims.sub || tokenClaims.preferred_username,
        username: username,
        email: auth.user?.profile?.email || tokenClaims.email || '',
        name: auth.user?.profile?.name || tokenClaims.name || `${auth.user?.profile?.given_name || tokenClaims.given_name || ''} ${auth.user?.profile?.family_name || tokenClaims.family_name || ''}`.trim(),
        given_name: auth.user?.profile?.given_name || tokenClaims.given_name,
        family_name: auth.user?.profile?.family_name || tokenClaims.family_name,
        // Store the complete OIDC user object for seamless switching
        oidcUser: auth.user,
      };
      
      console.log('[useAuth] Saving user session with OIDC data:', sessionData.username);
      
      // CRITICAL: Before saving, verify we're not about to overwrite the active session
      const oidcKey = getActiveOidcStorageKey();
      const currentOidcData = localStorage.getItem(oidcKey);
      if (currentOidcData) {
        const currentOidcUser = JSON.parse(currentOidcData);
        const currentOidcUsername = currentOidcUser.profile?.preferred_username || currentOidcUser.profile?.sub;
        console.log('[useAuth] Current OIDC session username:', currentOidcUsername);
        console.log('[useAuth] About to save session for:', username);
        
        if (currentOidcUsername !== username) {
          console.warn('[useAuth] ⚠️ WARNING: Trying to save a session that does NOT match the active OIDC session!');
          console.warn('[useAuth] This should not happen during normal authentication flow.');
          console.warn('[useAuth] Active OIDC user:', currentOidcUsername, '| Saving:', username);
        }
      }
      
      UserSessionStorage.saveSession(sessionData);
      lastSavedUsername.current = username;
      
      // Verify it was saved
      const allSessions = UserSessionStorage.getSessions();
      console.log('[useAuth] All saved sessions:', allSessions.length, 'users');
      allSessions.forEach(s => console.log(`  - ${s.username} (valid: ${UserSessionStorage.isSessionValid(s.username)})`));
      
      // Final verification: Check that the active OIDC session is still for the correct user
      const verifyOidcData = localStorage.getItem(oidcKey);
      if (verifyOidcData) {
        const verifyOidcUser = JSON.parse(verifyOidcData);
        const verifyUsername = verifyOidcUser.profile?.preferred_username || verifyOidcUser.profile?.sub;
        console.log('[useAuth] Final verification - Active OIDC user:', verifyUsername);
        if (verifyUsername !== username) {
          console.error('[useAuth] 🚨 CRITICAL: Active OIDC session changed during save! Was:', username, 'Now:', verifyUsername);
        }
      }
    } else if (!auth.isAuthenticated) {
      // Reset the tracking ref when user logs out
      if (lastSavedUsername.current !== null) {
        console.log('[useAuth] User logged out, resetting save tracker');
        lastSavedUsername.current = null;
      }
    }
  }, [auth.isAuthenticated, auth.user, tokenClaims]);

  // Monitor token changes to update saved sessions when tokens refresh
  useEffect(() => {
    // Get username from multiple sources (Okta and Keycloak compatible)
    const username = auth.user?.profile?.preferred_username || 
                     auth.user?.profile?.sub || 
                     tokenClaims?.sub;
    
    if (auth.isAuthenticated && auth.user && username) {
      // Check if this user has a saved session
      const existingSession = UserSessionStorage.getSession(username);
      
      if (existingSession) {
        // User already has a saved session - check if token changed
        const currentToken = auth.user.access_token;
        const savedToken = existingSession.oidcUser?.access_token;
        
        // If tokens are different, update the saved session (token was refreshed)
        if (currentToken && currentToken !== savedToken) {
          console.log('[useAuth] 🔄 Token changed for', username, '- updating saved session');
          
          const sessionData = {
            id: tokenClaims?.sub || username,
            username: username,
            email: auth.user?.profile?.email || tokenClaims?.email || '',
            name: auth.user?.profile?.name || tokenClaims?.name || `${auth.user?.profile?.given_name || tokenClaims?.given_name || ''} ${auth.user?.profile?.family_name || tokenClaims?.family_name || ''}`.trim(),
            given_name: auth.user?.profile?.given_name || tokenClaims?.given_name,
            family_name: auth.user?.profile?.family_name || tokenClaims?.family_name,
            oidcUser: auth.user,
          };
          
          UserSessionStorage.saveSession(sessionData);
          
          // Log token refresh details
          const now = Math.floor(Date.now() / 1000);
          const expiresAt = auth.user.expires_at || 0;
          const minutesUntilExpiry = Math.floor((expiresAt - now) / 60);
          
          console.log('[useAuth] ✅ Saved session updated with fresh token:', {
            user: username,
            hasRefreshToken: !!auth.user.refresh_token,
            expiresAt: new Date(expiresAt * 1000).toISOString(),
            minutesUntilExpiry,
          });
        }
      }
    }
  }, [auth.user?.access_token, auth.isAuthenticated, tokenClaims]);

  return {
    ...auth,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    user: auth.user ? {
      ...auth.user,
      profile: {
        ...auth.user.profile,
        // Merge token claims into profile
        email: auth.user.profile?.email || tokenClaims.email,
        name: auth.user.profile?.name || tokenClaims.name,
        preferred_username: auth.user.profile?.preferred_username || tokenClaims.preferred_username,
        given_name: auth.user.profile?.given_name || tokenClaims.given_name,
        family_name: auth.user.profile?.family_name || tokenClaims.family_name,
      }
    } : null,
    tokenClaims,
    roles,
    isAdmin,
    isSupport,
    isWorkflowAdmin,
    signIn: async (forceLogin: boolean = false) => {
      try {
        console.log('[useAuth] signIn called, forceLogin:', forceLogin);
        
        const addingAccount = sessionStorage.getItem('approvn_adding_account') === 'true';
        console.log('[useAuth] addingAccount flag:', addingAccount);
        
        if (addingAccount || forceLogin) {
          console.log('[useAuth] Adding new account flow initiated');
          
          // Save the current session before proceeding (if authenticated)
          if (auth.isAuthenticated && auth.user && tokenClaims?.preferred_username) {
            const username = tokenClaims.preferred_username;
            console.log('[useAuth] Saving current session for:', username);
            
            const sessionData = {
              id: tokenClaims.sub || username,
              username: username,
              email: auth.user?.profile?.email || tokenClaims.email || '',
              name: auth.user?.profile?.name || tokenClaims.name || `${auth.user?.profile?.given_name || tokenClaims.given_name || ''} ${auth.user?.profile?.family_name || tokenClaims.family_name || ''}`.trim(),
              given_name: auth.user?.profile?.given_name || tokenClaims.given_name,
              family_name: auth.user?.profile?.family_name || tokenClaims.family_name,
              oidcUser: auth.user,
            };
            
            UserSessionStorage.saveSession(sessionData);
            console.log('[useAuth] ✅ Current user session saved');
          }
          
          // KEEP the adding account flag so we can detect it after callback
          // Don't remove it here - let the callback handler remove it
          console.log('[useAuth] Keeping approvn_adding_account flag for callback detection');
          
          console.log('[useAuth] Initiating signin redirect with prompt=select_account');
        }
        
        // For "add another account", ask Okta to show its account chooser instead of
        // silently reusing the existing SSO session (which was returning the wrong user).
        // select_account is lighter-weight than prompt=login - it shouldn't force MFA
        // re-verification, just lets the user pick which Okta identity to use.
        console.log('[useAuth] Calling auth.signinRedirect()');
        if (addingAccount || forceLogin) {
          await auth.signinRedirect({ extraQueryParams: { prompt: 'select_account' } });
        } else {
          await auth.signinRedirect();
        }
        console.log('[useAuth] signinRedirect() call completed');
        
      } catch (error) {
        console.error('[useAuth] Sign in error:', error);
        console.error('[useAuth] Error details:', {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined
        });
        // Clean up on error
        sessionStorage.removeItem('approvn_adding_account');
        // Try signin anyway as fallback
        try {
          await auth.signinRedirect();
        } catch (fallbackError) {
          console.error('[useAuth] Fallback signin also failed:', fallbackError);
        }
      }
    },
    signOut: async () => {
      try {
        console.log('[useAuth] Signing out...');
        console.log('[useAuth] Current user:', tokenClaims?.preferred_username);
        
        // Capture the id_token before wiping storage - Okta's /v1/logout endpoint needs
        // id_token_hint to validate the request; without it Okta returns 400 Bad Request
        // even when client_id + post_logout_redirect_uri are present.
        const idTokenHint = auth.user?.id_token;
        
        // Clear the OIDC user data from localStorage BEFORE redirecting
        // This prevents auto-login after coming back from logout
        const oidcKey = getActiveOidcStorageKey();
        const storageKey = 'approvn_user_sessions';
        
        // Save the stored sessions (we want to keep these)
        const savedSessions = localStorage.getItem(storageKey);
        
        // Clear all OIDC keys
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('oidc.')) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => {
          localStorage.removeItem(key);
          console.log('[useAuth] Removed OIDC key:', key);
        });
        
        // Restore the saved sessions
        if (savedSessions) {
          localStorage.setItem(storageKey, savedSessions);
          console.log('[useAuth] Preserved saved sessions');
        }
        
        // Reset the save tracker
        lastSavedUsername.current = null;
        console.log('[useAuth] Reset save tracker');
        console.log('[useAuth] Redirecting to OIDC provider logout...');
        
        // Now redirect to OIDC provider logout, passing id_token_hint explicitly since
        // the local user session was just cleared above
        await auth.signoutRedirect(idTokenHint ? { id_token_hint: idTokenHint } : undefined);
      } catch (error) {
        console.error('Sign out error:', error);
      }
    },
  };
}
